import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import PageHeader from '@components/shared/PageHeader';
import { UserDashboard } from './components/UserDashboard';
import { CoursesTab } from './components/CoursesTab';
import { ProductsTab } from './components/ProductsTab';
import type { CourseDetail } from './components/CourseDetailModal';
import { mapMigrationPlanToAdminUserView } from './legacy/mapMigrationPlanToAdminUserView';
import {
  fetchLegacyUser,
  migrateLegacyUser,
  type LegacyMigratePlanConfiguration,
  type LegacyMigratePlanPatient,
  type LegacyMigratePreview,
  type LegacyMigrationRemaps,
} from '../services/legacyUserManager';
import {
  buildProfileFromLegacyDetail,
  mergeMigrationProfiles,
} from './legacy/buildProfileFromLegacyDetail';
import {
  buildClinicalFromLegacyDetail,
  mergeClinicalIntoPlan,
} from './legacy/buildClinicalFromLegacyDetail';

const previewMutationNotice = () => {
  notifications.show({
    color: 'gray',
    message: 'Anteprima migrazione — nessuna mutazione su be2025',
  });
};

const AdminLegacyUserMigrationPreview = () => {
  const { id } = useParams<{ id: string }>();
  const userId = id ? Number(id) : NaN;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [migrateWarning, setMigrateWarning] = useState<string | null>(null);
  const [plan, setPlan] = useState<LegacyMigratePreview['plan'] | null>(null);

  useEffect(() => {
    if (!Number.isFinite(userId) || userId <= 0) {
      setError('Invalid legacy user id');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setMigrateWarning(null);
    setPlan(null);

    void (async () => {
      try {
        let result: Awaited<ReturnType<typeof migrateLegacyUser>> | null = null;
        let detail: Awaited<ReturnType<typeof fetchLegacyUser>> | null = null;
        let migrateError: string | null = null;
        let detailError: string | null = null;
        try {
          result = await migrateLegacyUser(userId, { dryRun: true });
        } catch (err) {
          migrateError = (err as Error).message || 'migrate failed';
        }
        try {
          detail = await fetchLegacyUser(userId);
        } catch (err) {
          detailError = (err as Error).message || 'detail failed';
        }
        if (cancelled) return;
        if (!detail) {
          setError(
            migrateError ||
              detailError ||
              'Failed to load legacy user detail'
          );
          return;
        }

        const detailProfile = buildProfileFromLegacyDetail(detail);
        const migratePlan =
          result && 'dryRun' in result && result.dryRun ? result.plan : null;
        const mergedProfile = mergeMigrationProfiles(
          migratePlan?.profile,
          detailProfile
        );
        const clinical = buildClinicalFromLegacyDetail(detail);

        const basePlan: LegacyMigratePreview['plan'] = migratePlan ?? {
          email:
            typeof detail.user?.email === 'string'
              ? detail.user.email
              : mergedProfile.full_name || `legacy-${userId}`,
          first_name: mergedProfile.first_name,
          last_name: mergedProfile.last_name,
          full_name: mergedProfile.full_name,
          legacy_user_id: userId,
          created_at: mergedProfile.created_at ?? null,
          profile: mergedProfile,
          courses: [],
          products: [],
          remaps: { courses: [], videos: [], answers_by_course: [] },
          report: {
            coursesMapped: 0,
            videosMapped: 0,
            answersMapped: 0,
            warnings: migrateError
              ? [`migrate dry-run failed: ${migrateError}`]
              : [],
          },
        };

        const withClinical = mergeClinicalIntoPlan(basePlan, clinical);
        const enrichedPlan: LegacyMigratePreview['plan'] = {
          ...withClinical,
          profile: mergedProfile,
          created_at: mergedProfile.created_at ?? basePlan.created_at ?? null,
          first_name: mergedProfile.first_name ?? basePlan.first_name,
          last_name: mergedProfile.last_name ?? basePlan.last_name,
          full_name: mergedProfile.full_name ?? basePlan.full_name,
          remaps: basePlan.remaps ?? {
            courses: [],
            videos: [],
            answers_by_course: [],
          },
        };

        setPlan(enrichedPlan);
        if (migrateError) {
          setMigrateWarning(migrateError);
          setError(null);
        }      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const view = useMemo(() => {
    if (!plan) return null;
    try {
      return mapMigrationPlanToAdminUserView(plan);
    } catch {
      return null;
    }
  }, [plan]);

  const handleEditCourse = useCallback((_course: CourseDetail) => {
    previewMutationNotice();
  }, []);

  const handleUpdateProduct = useCallback(
    (_productId: number, _date: Date | string | null) => {
      previewMutationNotice();
    },
    []
  );

  const handleRemoveProduct = useCallback((_productId: number) => {
    previewMutationNotice();
  }, []);

  const handleRefreshProducts = useCallback(() => {
    previewMutationNotice();
  }, []);

  const productsForTab = useMemo(() => {
    if (!view) return [];
    return view.userProducts.map((p) => ({
      product_id: p.product_id,
      name: p.name,
      category: p.category,
      experience_category: p.experience_category,
      until_date: p.until_date ?? undefined,
    }));
  }, [view]);

  const patients = plan?.patients ?? [];
  const configurations = plan?.configurations ?? [];
  const exposureCount = configurations.filter((c) => c.type === 'exposure')
    .length;
  const performanceCount = configurations.filter(
    (c) => c.type === 'performance'
  ).length;
  const itemFeedbackTotal = configurations.reduce(
    (n, c) => n + (c.food_feedback_count ?? 0),
    0
  );
  const hrSamplesTotal = configurations.reduce(
    (n, c) => n + (c.hr_sample_count ?? 0),
    0
  );

  return (
    <Container size='xl' py='md'>
      <Stack gap='lg'>
        <PageHeader
          title='Anteprima migrazione → UI v2'
          subtitle={
            Number.isFinite(userId)
              ? `Come apparirebbe /admin/users/:id (+ corsi/prodotti) dopo il mapping (vapor #${userId})`
              : 'Legacy user preview'
          }
        />

        <Alert color='blue' title='Dry-run — nessun dato scritto su be2025'>
          Stesso layout di{' '}
          <Text span ff='monospace'>
            /admin/users/:id
          </Text>
          , più le tabelle di{' '}
          <Text span ff='monospace'>
            /courses
          </Text>{' '}
          e{' '}
          <Text span ff='monospace'>
            /products
          </Text>
          . Edit/remove sono disabilitati (solo anteprima).
        </Alert>

        {migrateWarning ? (
          <Alert color='yellow' title='Migrate dry-run fallito — preview da detail'>
            Patients/configurations arricchiti dal dettaglio vapor. Corsi/prodotti
            possono essere incompleti finché lo staging non remappa i food id.
            <Text size='sm' mt='xs' ff='monospace'>
              {migrateWarning}
            </Text>
          </Alert>
        ) : null}

        {loading ? (
          <Group justify='center' py='xl'>
            <Loader />
            <Text c='dimmed'>Calcolo mapping…</Text>
          </Group>
        ) : null}

        {error ? (
          <Alert color='red' title='Preview fallita'>
            {error}
          </Alert>
        ) : null}

        {view ? (
          <>
            <Group gap='xs' wrap='wrap'>
              <Badge color='teal' variant='light'>
                {view.counts.courses} corsi
              </Badge>
              <Badge color='cyan' variant='light'>
                {view.counts.courseProducts} prodotti corso
              </Badge>
              <Badge color='blue' variant='light'>
                {view.counts.videoProducts} prodotti video
              </Badge>
              <Badge color='orange' variant='light'>
                {view.counts.clinicalProducts} life suite
              </Badge>
              <Badge color='grape' variant='light'>
                {view.report.answersMapped} answers
              </Badge>
              <Badge color='orange' variant='light'>
                {patients.length} patients
              </Badge>
              <Badge color='pink' variant='light'>
                {exposureCount} exposure
              </Badge>
              <Badge color='violet' variant='light'>
                {performanceCount} performance
              </Badge>
              <Badge color='teal' variant='light'>
                {itemFeedbackTotal} food feedback
              </Badge>
              <Badge color='red' variant='light'>
                {hrSamplesTotal} HR samples
              </Badge>
              <Text size='sm' c='dimmed'>
                {view.userInfo.email}
              </Text>
            </Group>

            {(plan?.report?.warnings?.length ?? 0) > 0 ? (
              <Alert color='orange' title='Migration warnings'>
                <Stack gap={4}>
                  {plan!.report.warnings.map((w) => (
                    <Text key={w} size='sm'>
                      {w}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            <UserDashboard
              userInfo={view.userInfo}
              userProducts={productsForTab}
              userCourses={view.userCourses}
              hasManageProducts={false}
              hasManageCourses={false}
              hasManagePermissions={false}
              hasManageDevices={false}
              hasDev={false}
              hasAnalytics={false}
              previewMode
              backTo={`/admin/legacy-users/${userId}`}
              backLabel='Torna al dettaglio legacy'
            />

            <Card withBorder>
              <Stack gap='md'>
                <Group justify='space-between' align='center' wrap='wrap'>
                  <div>
                    <Title order={3}>Corsi</Title>
                    <Text size='sm' c='dimmed'>
                      Stessa tabella di /admin/users/:id/courses (read-only)
                    </Text>
                  </div>
                  <Badge color='teal' variant='light'>
                    {view.counts.courses}
                  </Badge>
                </Group>
                <CoursesTab
                  userCourses={view.userCourses as CourseDetail[]}
                  loadingCourses={false}
                  onEditCourse={handleEditCourse}
                />
              </Stack>
            </Card>

            <Card withBorder>
              <Stack gap='md'>
                <Group justify='space-between' align='center' wrap='wrap'>
                  <div>
                    <Title order={3}>Prodotti</Title>
                    <Text size='sm' c='dimmed'>
                      Stessa tabella di /admin/users/:id/products — category{' '}
                      <Text span ff='monospace'>
                        video
                      </Text>{' '}
                      = entitlement video vapor
                    </Text>
                  </div>
                  <Group gap='xs'>
                    <Badge color='cyan' variant='light'>
                      {view.counts.courseProducts} course
                    </Badge>
                    <Badge color='blue' variant='light'>
                      {view.counts.videoProducts} video
                    </Badge>
                    <Badge color='orange' variant='light'>
                      {view.counts.clinicalProducts} life suite
                    </Badge>
                  </Group>
                </Group>
                <ProductsTab
                  userProducts={productsForTab}
                  loadingProducts={false}
                  userId={undefined}
                  onRefresh={handleRefreshProducts}
                  onUpdateProduct={handleUpdateProduct}
                  onRemoveProduct={handleRemoveProduct}
                />
              </Stack>
            </Card>

            <PatientsSection patients={patients} />
            <ConfigurationsSection
              configurations={configurations}
              patients={patients}
            />

            <RemapsSection remaps={view.remaps} />
          </>
        ) : null}
      </Stack>
    </Container>
  );
};

const PatientsSection = ({
  patients,
}: {
  patients: LegacyMigratePlanPatient[];
}) => (
  <Card withBorder>
    <Stack gap='md'>
      <Group justify='space-between' align='center' wrap='wrap'>
        <div>
          <Title order={3}>Patients</Title>
          <Text size='sm' c='dimmed'>
            Stesso <Text span ff='monospace'>code</Text> vapor → be2025
            Patients (nuovo id)
          </Text>
        </div>
        <Badge color='orange' variant='light'>
          {patients.length}
        </Badge>
      </Group>
      {patients.length === 0 ? (
        <Text size='sm' c='dimmed'>
          Nessun patient nel mapping
        </Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vapor id</Table.Th>
              <Table.Th>Code</Table.Th>
              <Table.Th>created_at</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {patients.map((p) => (
              <Table.Tr key={`pt-${p.vapor_patient_id}`}>
                <Table.Td>#{p.vapor_patient_id}</Table.Td>
                <Table.Td>
                  <Text ff='monospace' size='sm'>
                    {p.code}
                  </Text>
                </Table.Td>
                <Table.Td>{p.created_at ?? '—'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  </Card>
);

const formatConfigSummary = (cfg: LegacyMigratePlanConfiguration): string => {
  const outcomeBits: string[] = [];
  if ((cfg.food_feedback_count ?? 0) > 0) {
    outcomeBits.push(`${cfg.food_feedback_count} food feedback`);
  }
  if ((cfg.hr_sample_count ?? 0) > 0) {
    outcomeBits.push(`${cfg.hr_sample_count} HR samples`);
  }
  if (cfg.type === 'exposure') {
    const foodIds = Array.isArray(cfg.config.itemIds)
      ? (cfg.config.itemIds as number[])
      : [];
    const foodNames = Array.isArray(cfg.config.foodNames)
      ? (cfg.config.foodNames as string[])
      : [];
    const multiMode =
      cfg.config.multiItem || cfg.config.isMultiItem
        ? 'multi-item'
        : 'single-item';
    const listName =
      typeof cfg.config.vapor_food_list_name === 'string'
        ? cfg.config.vapor_food_list_name
        : null;
    const foodsLabel =
      foodNames.length > 0
        ? foodNames.join(', ')
        : `itemIds: [${foodIds.join(', ')}]`;
    return [listName ? `list: ${listName}` : null, foodsLabel, multiMode, ...outcomeBits]
      .filter(Boolean)
      .join(' · ');
  }
  return [
    `hr ${cfg.config.hr_min ?? '—'}–${cfg.config.hr_max ?? '—'}`,
    `diff ${cfg.config.difficulty ?? '—'}`,
    `start ${cfg.config.start ?? 0}`,
    ...outcomeBits,
  ].join(' · ');
};

const ConfigurationsSection = ({
  configurations,
  patients,
}: {
  configurations: LegacyMigratePlanConfiguration[];
  patients: LegacyMigratePlanPatient[];
}) => {
  const codeByPatient = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of patients) {
      map.set(p.vapor_patient_id, p.code);
    }
    return map;
  }, [patients]);

  const exposure = configurations.filter((c) => c.type === 'exposure').length;
  const performance = configurations.filter(
    (c) => c.type === 'performance'
  ).length;

  return (
    <Card withBorder>
      <Stack gap='md'>
        <Group justify='space-between' align='center' wrap='wrap'>
          <div>
            <Title order={3}>Configurations</Title>
            <Text size='sm' c='dimmed'>
              DeviceConfigurations — exposure (food list) / performance (HR)
            </Text>
          </div>
          <Group gap='xs'>
            <Badge color='pink' variant='light'>
              {exposure} exposure
            </Badge>
            <Badge color='violet' variant='light'>
              {performance} performance
            </Badge>
          </Group>
        </Group>
        {configurations.length === 0 ? (
          <Text size='sm' c='dimmed'>
            Nessuna configuration nel mapping
          </Text>
        ) : (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vapor id</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Scenario</Table.Th>
                <Table.Th>Patient</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Config</Table.Th>
                <Table.Th>created_at</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {configurations.map((c) => (
                <Table.Tr key={`cfg-${c.vapor_configuration_id}`}>
                  <Table.Td>#{c.vapor_configuration_id}</Table.Td>
                  <Table.Td>{c.type}</Table.Td>
                  <Table.Td>{c.scenario}</Table.Td>
                  <Table.Td>
                    {codeByPatient.get(c.vapor_patient_id) ??
                      `#${c.vapor_patient_id}`}
                  </Table.Td>
                  <Table.Td>{c.status}</Table.Td>
                  <Table.Td>
                    <Text size='xs'>{formatConfigSummary(c)}</Text>
                  </Table.Td>
                  <Table.Td>{c.created_at ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
};

const RemapsSection = ({ remaps }: { remaps: LegacyMigrationRemaps }) => {
  if (
    remaps.courses.length === 0 &&
    remaps.videos.length === 0 &&
    remaps.answers_by_course.length === 0
  ) {
    return null;
  }

  return (
    <Card withBorder>
      <Stack gap='md'>
        <Title order={4}>Remaps (vapor → be2025)</Title>

        {remaps.courses.length > 0 ? (
          <Stack gap='xs'>
            <Text fw={600} size='sm'>
              Courses
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vapor</Table.Th>
                  <Table.Th>be2025</Table.Th>
                  <Table.Th>Product</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {remaps.courses.map((row) => (
                  <Table.Tr key={`c-${row.vapor_id}`}>
                    <Table.Td>
                      #{row.vapor_id} {row.vapor_name}
                    </Table.Td>
                    <Table.Td>
                      #{row.be_id} {row.be_name}
                    </Table.Td>
                    <Table.Td>{row.product_id ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        ) : null}

        {remaps.videos.length > 0 ? (
          <Stack gap='xs'>
            <Text fw={600} size='sm'>
              Videos
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vapor</Table.Th>
                  <Table.Th>be video</Table.Th>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Until</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {remaps.videos.map((row) => (
                  <Table.Tr key={`v-${row.vapor_id}`}>
                    <Table.Td>
                      #{row.vapor_id} {row.vapor_name}
                    </Table.Td>
                    <Table.Td>#{row.be_video_id}</Table.Td>
                    <Table.Td>{row.product_id}</Table.Td>
                    <Table.Td>{row.until_date ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        ) : null}

        {remaps.answers_by_course.length > 0 ? (
          <Stack gap='xs'>
            <Text fw={600} size='sm'>
              Answers by course
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vapor course</Table.Th>
                  <Table.Th>be course</Table.Th>
                  <Table.Th>Attempts</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {remaps.answers_by_course.map((row) => (
                  <Table.Tr
                    key={`a-${row.vapor_course_id}-${row.be_course_id}`}
                  >
                    <Table.Td>#{row.vapor_course_id}</Table.Td>
                    <Table.Td>#{row.be_course_id}</Table.Td>
                    <Table.Td>{row.attempts}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
};

export default AdminLegacyUserMigrationPreview;
